/**
 * Service for validating game data integrity and managing data versions
 * Handles schema validation, data consistency checks, and migration support
 */

import { z } from 'zod';
import { BodyLocation, WeaponClass } from '../types/character';
import { DamageType } from '../types/dice';
import {
  PROSE_DAMAGE_PROFILES,
  isProseDamageProfile,
  parseDamageTypeName,
  resolveDamageType
} from '../utils/parseDamageType';

/**
 * weaponClass values that are not personal WeaponClass members but are real
 * source data: vehicle, walker and battlesuit mounts from Deathwatch, Rites of
 * Battle and the Tau entries. normalizeWeaponClass returns null for these and
 * the class is derived from subtype instead.
 */
const MOUNT_WEAPON_CLASSES: readonly string[] = [
  'vehicle',
  'dreadnought',
  'suit basic',
  'suit heavy',
  'suit melee',
  'melee/thrown',
  'xv104 only'
];

function isKnownWeaponClass(value: string): boolean {
  const v = value.trim().toLowerCase();
  return (Object.values(WeaponClass) as string[]).includes(v) || MOUNT_WEAPON_CLASSES.includes(v);
}

/**
 * True when a damage expression actually rolls or deals damage. Weapons whose
 * profile is prose-described store "Special", "Varies" or "-" and legitimately
 * have no damage type.
 */
function hasDamageRoll(damage?: string): boolean {
  if (!damage) return false;
  const d = damage.trim();
  if (d === '-' || d === '') return false;
  if (isProseDamageProfile(d)) return false;
  return /\d/.test(d);
}

// Base schema for all game data files
const BaseDataFileSchema = z.object({
  version: z.string(),
  schemaVersion: z.number(),
  lastUpdated: z.string().datetime()
});

// Interface for data with metadata
interface DataWithMetadata {
  version?: string;
  schemaVersion?: number;
  lastUpdated?: string;
  [key: string]: unknown;
}

// Interface for items with IDs
interface ItemWithId {
  id: string;
  [key: string]: unknown;
}

// Interface for items with starting skills/talents/equipment
interface ItemWithReferences extends ItemWithId {
  startingSkills?: string[];
  startingTalents?: string[];
  startingEquipment?: string[];
  startingTraits?: string[];
  rulebook?: string;
}

// Individual item schemas
const CareerSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  startingSkills: z.array(z.string()),
  startingTalents: z.array(z.string()),
  startingEquipment: z.array(z.string()),
  aptitudes: z.array(z.string()),
  rulebook: z.string()
});

const HomeworldSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  characteristicModifiers: z.record(z.number()),
  startingSkills: z.array(z.string()),
  startingTalents: z.array(z.string()),
  startingTraits: z.array(z.string()),
  startingEquipment: z.array(z.string()),
  rulebook: z.string()
});

const BirthrightSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  benefit: z.string(),
  startingEquipment: z.array(z.string()).optional(),
  rulebook: z.string()
});

const SkillSchema = z.object({
  id: z.string(),
  name: z.string(),
  characteristic: z.string(),
  category: z.string().optional(),
  type: z.string().optional(),
  descriptor: z.string().optional(),
  isSpecialist: z.boolean().optional(),
  shortDescription: z.string().optional(),
  fullDescription: z.string().optional(),
  skillUseDuration: z.string().optional(),
  specialUses: z.array(z.any()).optional(),
  aptitudes: z.array(z.string()).optional(),
  specializations: z.array(z.string()).optional(),
  rulebook: z.string()
});

const TalentSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.union([z.string(), z.number()]).optional(),
  prerequisites: z.union([z.array(z.any()), z.string()]).optional(),
  isSpecialized: z.boolean().optional(),
  shortDescription: z.string().optional(),
  fullDescription: z.string().optional(),
  cost: z.number().optional(),
  aptitudes: z.array(z.string()).optional(),
  effect: z.union([z.string(), z.record(z.any())]).optional(),
  maxPurchases: z.union([z.number(), z.string()]).optional(),
  rulebook: z.string()
});

const WeaponSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('weapon'),
  class: z.string(),
  range: z.number(),
  rateOfFire: z.object({
    single: z.number(),
    semiAuto: z.number(),
    fullAuto: z.number()
  }),
  damage: z.string(),
  penetration: z.number(),
  clip: z.number(),
  reload: z.string(),
  special: z.array(z.string()),
  weight: z.number(),
  availability: z.string(),
  cost: z.number(),
  description: z.string(),
  rulebook: z.string()
});

const ArmorSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('armor'),
  locations: z.array(z.string()),
  armorPoints: z.number(),
  weight: z.number(),
  availability: z.string(),
  cost: z.number(),
  special: z.array(z.string()),
  description: z.string(),
  rulebook: z.string()
});

/**
 * items.json - the unified item catalogue (1721 entries: gear, armor, weapon,
 * cybernetic). This file replaced the separate weapons/armor/equipment data files
 * but no validator was ever added for it, so nothing checked it at load. That is
 * how four Faith and Coin armour entries shipped locations of "arms" and "legs",
 * which are not BodyLocation members and silently matched no hit location.
 *
 * The shapes below were derived from the actual file, not from the TypeScript
 * interfaces - several fields genuinely carry more than one type:
 *   rulebook          string in 1688 entries, string[] in 33
 *   weaponStats.special  array in 478, comma-separated string in 268
 *   penetration/clip  number mostly, but "Special" in 29 / 21 entries
 *   rateOfFire.*      each slot is string | null
 */
const ItemArmorDataSchema = z.object({
  // The whole point: locations must be real BodyLocation members.
  locations: z.array(z.nativeEnum(BodyLocation)),
  armorPoints: z.number(),
  special: z.array(z.string())
});

const ItemWeaponStatsSchema = z
  .object({
    range: z.string().optional(),
    rateOfFire: z
      .object({
        single: z.string().nullable().optional(),
        semiAuto: z.string().nullable().optional(),
        fullAuto: z.string().nullable().optional(),
        display: z.string().optional()
      })
      .optional(),
    damage: z.string().optional(),
    // items.json stores Title Case ("Rending") while DamageType is lowercase, and
    // 17 weapons with no damage roll store a prose profile ("Special", "Snare",
    // "Varies") instead. Both are valid source data, so accept anything that
    // either resolves to a DamageType or is a documented prose profile.
    damageType: z
      .string()
      .refine((v) => parseDamageTypeName(v) !== undefined || isProseDamageProfile(v), {
        message: `damageType must resolve to one of ${Object.values(DamageType).join(', ')} (case-insensitive), or be a prose profile (${PROSE_DAMAGE_PROFILES.join(', ')})`
      })
      .optional(),
    // "Special" appears where a weapon's profile is prose-described
    penetration: z.union([z.number(), z.string()]).optional(),
    clip: z.union([z.number(), z.string()]).nullable().optional(),
    reload: z.string().optional(),
    reloadActions: z.number().optional(),
    rechargeTime: z.number().optional(),
    special: z.union([z.string(), z.array(z.string())]).optional(),
    specialParams: z.record(z.unknown()).optional()
  })
  .passthrough();

const ItemSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['gear', 'armor', 'weapon', 'cybernetic']),
    subtype: z.string(),
    weight: z.number(),
    cost: z.number().nullable(),
    description: z.string(),
    rulebook: z.union([z.string(), z.array(z.string())]),
    // 7 Faith and Coin weapons omit this - see TODO.md, kept optional so the
    // structural check stays green and real failures stand out
    availability: z.string().optional(),
    section: z.string().optional(),
    proficient: z.boolean().optional(),
    proficiencyRequired: z.string().optional(),
    weaponClass: z
      .string()
      .refine((v) => isKnownWeaponClass(v), {
        message: `weaponClass must be one of ${Object.values(WeaponClass).join(', ')} or a documented mount class (${MOUNT_WEAPON_CLASSES.join(', ')})`
      })
      .optional(),
    npcOnly: z.boolean().optional(),
    weaponModification: z.boolean().optional(),
    armorModification: z.boolean().optional(),
    craftsmanship: z.string().optional(),
    mechadendriteType: z.string().optional(),
    tags: z.array(z.string()).optional(),
    armorData: ItemArmorDataSchema.optional(),
    weaponStats: ItemWeaponStatsSchema.optional()
  })
  .passthrough()
  .superRefine((item, ctx) => {
    if (item.type === 'armor' && !item.armorData) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `armour item "${item.id}" has no armorData`
      });
    }
    // 4 armour entries still carry a legacy top-level `locations` alongside
    // armorData. The loader reads armorData, so the duplicate can drift.
    if ('locations' in item) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `item "${item.id}" has a legacy top-level "locations" field; move it into armorData`
      });
    }
    // A weapon that rolls damage should have a resolvable damage type, from either
    // weaponStats.damageType or the rulebook letter on the damage expression
    // ("1d10 R"). Nothing defaults this any more, so a gap means the weapon reaches
    // combat with no damage type. Reported as a warning rather than an error
    // because for the remaining cases the source table omits the letter too, so
    // the value cannot be invented - see validateItemsData and TODO.md.
  });

const ItemsDataSchema = BaseDataFileSchema.extend({
  items: z.array(ItemSchema)
});

const EquipmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  weight: z.number(),
  availability: z.string(),
  cost: z.number().optional(),
  description: z.string(),
  rulebook: z.string()
});

const TraitSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  effect: z.string().optional(),
  rulebook: z.string()
});

// Complete data file schemas
const CareersDataSchema = BaseDataFileSchema.extend({
  careers: z.array(CareerSchema)
});

const HomeworldsDataSchema = BaseDataFileSchema.extend({
  homeworlds: z.array(HomeworldSchema)
});

const BirthrightsDataSchema = BaseDataFileSchema.extend({
  birthrights: z.array(BirthrightSchema)
});

const LuresDataSchema = BaseDataFileSchema.extend({
  lures: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      benefit: z.string(),
      rulebook: z.string()
    })
  )
});

const TrialsDataSchema = BaseDataFileSchema.extend({
  trials: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      benefit: z.string(),
      drawback: z.string().optional(),
      rulebook: z.string()
    })
  )
});

const MotivationsDataSchema = BaseDataFileSchema.extend({
  motivations: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      benefit: z.string(),
      rulebook: z.string()
    })
  )
});

const SkillsDataSchema = BaseDataFileSchema.extend({
  skills: z.array(SkillSchema)
});

const TalentsDataSchema = BaseDataFileSchema.extend({
  talents: z.array(TalentSchema)
});

const WeaponsDataSchema = BaseDataFileSchema.extend({
  weapons: z.array(WeaponSchema)
});

const ArmorDataSchema = BaseDataFileSchema.extend({
  armor: z.array(ArmorSchema)
});

const EquipmentDataSchema = BaseDataFileSchema.extend({
  equipment: z.array(EquipmentSchema)
});

const TraitsDataSchema = BaseDataFileSchema.extend({
  traits: z.array(TraitSchema)
});

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface DataIntegrityResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  duplicateIds: string[];
  missingReferences: string[];
  orphanedReferences: string[];
}

export class DataValidationService {
  private static instance: DataValidationService;

  private readonly CURRENT_SCHEMA_VERSION = 1;
  private readonly CURRENT_DATA_VERSION = '1.0.0';

  private constructor() {}

  public static getInstance(): DataValidationService {
    if (!DataValidationService.instance) {
      DataValidationService.instance = new DataValidationService();
    }
    return DataValidationService.instance;
  }

  /**
   * Validate a careers data file
   */
  public validateCareersData(data: unknown): ValidationResult {
    return this.validateWithSchema(CareersDataSchema, data, 'careers');
  }

  /**
   * Validate a homeworlds data file
   */
  public validateHomeworldsData(data: unknown): ValidationResult {
    return this.validateWithSchema(HomeworldsDataSchema, data, 'homeworlds');
  }

  /**
   * Validate a birthrights data file
   */
  public validateBirthrightsData(data: unknown): ValidationResult {
    return this.validateWithSchema(BirthrightsDataSchema, data, 'birthrights');
  }

  /**
   * Validate a lures data file
   */
  public validateLuresData(data: unknown): ValidationResult {
    return this.validateWithSchema(LuresDataSchema, data, 'lures');
  }

  /**
   * Validate a trials data file
   */
  public validateTrialsData(data: unknown): ValidationResult {
    return this.validateWithSchema(TrialsDataSchema, data, 'trials');
  }

  /**
   * Validate a motivations data file
   */
  public validateMotivationsData(data: unknown): ValidationResult {
    return this.validateWithSchema(MotivationsDataSchema, data, 'motivations');
  }

  /**
   * Validate a skills data file
   */
  public validateSkillsData(data: unknown): ValidationResult {
    return this.validateWithSchema(SkillsDataSchema, data, 'skills');
  }

  /**
   * Validate a talents data file
   */
  public validateTalentsData(data: unknown): ValidationResult {
    return this.validateWithSchema(TalentsDataSchema, data, 'talents');
  }

  /**
   * Validate a weapons data file
   */
  public validateWeaponsData(data: unknown): ValidationResult {
    return this.validateWithSchema(WeaponsDataSchema, data, 'weapons');
  }

  /**
   * Validate an armor data file
   */
  public validateArmorData(data: unknown): ValidationResult {
    return this.validateWithSchema(ArmorDataSchema, data, 'armor');
  }

  /**
   * Validate items.json, the unified item catalogue
   */
  public validateItemsData(data: unknown): ValidationResult {
    const result = this.validateWithSchema(ItemsDataSchema, data, 'items');

    // Completeness gaps are warnings, not errors: the values cannot be invented,
    // and a permanently-failing validator trains people to ignore it.
    const items = (data as { items?: unknown[] })?.items;
    if (Array.isArray(items)) {
      for (const raw of items) {
        const item = raw as {
          id?: string;
          type?: string;
          weaponStats?: unknown;
          weaponClass?: unknown;
          availability?: unknown;
        };
        if (item.type === 'weapon' && !item.weaponStats) {
          result.warnings.push(`weapon "${item.id}" has no weaponStats`);
        }
        if (item.type === 'weapon' && item.weaponStats) {
          const stats = item.weaponStats as { damage?: string; damageType?: string };
          // Prose-profile weapons (webbers, graviton guns, shock nets) have no
          // damage type by design. Recorded so the count is visible rather than
          // silently coerced to Impact.
          if (isProseDamageProfile(stats.damageType)) {
            result.warnings.push(
              `weapon "${item.id}" has a prose damage profile ("${stats.damageType}") and no damage type`
            );
          } else if (hasDamageRoll(stats.damage) && !resolveDamageType(stats)) {
            // Nothing defaults the damage type any more, so these reach combat
            // without one. The source tables omit the letter for these rows too,
            // so the value has to come from a human, not a guess.
            result.warnings.push(
              `weapon "${item.id}" rolls damage (${stats.damage}) but has no resolvable damage type; add weaponStats.damageType or a rulebook letter (e.g. "${stats.damage} R")`
            );
          }
          if (item.weaponClass === undefined) {
            result.warnings.push(
              `weapon "${item.id}" has no weaponClass; class will be derived from subtype`
            );
          }
        }
        if (item.availability === undefined) {
          result.warnings.push(`item "${item.id}" has no availability rating`);
        }
      }
    }
    return result;
  }

  /**
   * Validate an equipment data file
   */
  public validateEquipmentData(data: unknown): ValidationResult {
    return this.validateWithSchema(EquipmentDataSchema, data, 'equipment');
  }

  /**
   * Validate a traits data file
   */
  public validateTraitsData(data: unknown): ValidationResult {
    return this.validateWithSchema(TraitsDataSchema, data, 'traits');
  }

  /**
   * Generic schema validation helper
   */
  private validateWithSchema(
    schema: z.ZodSchema,
    data: unknown,
    dataType: string
  ): ValidationResult {
    try {
      schema.parse(data);
      return {
        isValid: true,
        errors: [],
        warnings: this.checkVersionWarnings(data, dataType)
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return {
          isValid: false,
          errors: error.errors.map((err) => `${err.path.join('.')}: ${err.message}`),
          warnings: []
        };
      }
      return {
        isValid: false,
        errors: [`Unknown validation error: ${error}`],
        warnings: []
      };
    }
  }

  /**
   * Check for version-related warnings
   */
  private checkVersionWarnings(data: unknown, dataType: string): string[] {
    const warnings: string[] = [];
    const dataWithMeta = data as DataWithMetadata;

    if (dataWithMeta.schemaVersion && dataWithMeta.schemaVersion < this.CURRENT_SCHEMA_VERSION) {
      warnings.push(
        `${dataType} schema version ${dataWithMeta.schemaVersion} is outdated (current: ${this.CURRENT_SCHEMA_VERSION})`
      );
    }

    // Only warn if data version is lower than the baseline version
    if (
      dataWithMeta.version &&
      this.isVersionLower(dataWithMeta.version, this.CURRENT_DATA_VERSION)
    ) {
      warnings.push(
        `${dataType} data version ${dataWithMeta.version} may be outdated (baseline: ${this.CURRENT_DATA_VERSION})`
      );
    }

    const lastUpdated = dataWithMeta.lastUpdated ? new Date(dataWithMeta.lastUpdated) : new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    if (lastUpdated < sixMonthsAgo) {
      warnings.push(
        `${dataType} data was last updated ${lastUpdated.toDateString()}, consider reviewing for updates`
      );
    }

    return warnings;
  }

  /**
   * Validate data integrity across all game data files
   */
  public validateDataIntegrity(gameData: {
    careers: unknown[];
    homeworlds: unknown[];
    birthrights: unknown[];
    lures: unknown[];
    trials: unknown[];
    motivations: unknown[];
    skills: unknown[];
    talents: unknown[];
    weapons: unknown[];
    armor: unknown[];
    equipment: unknown[];
    traits: unknown[];
    shopItems?: unknown[];
  }): DataIntegrityResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const duplicateIds: string[] = [];
    const missingReferences: string[] = [];
    const orphanedReferences: string[] = [];

    // Check for duplicate IDs across all data
    const allIds = new Set<string>();
    const checkDuplicates = (items: unknown[], category: string) => {
      items.forEach((item) => {
        const itemWithId = item as ItemWithId;
        const itemId = itemWithId.id;
        if (allIds.has(itemId)) {
          duplicateIds.push(`${category}: ${itemId}`);
          errors.push(`Duplicate ID found: ${itemId} in ${category}`);
        }
        allIds.add(itemId);
      });
    };

    checkDuplicates(gameData.careers, 'careers');
    checkDuplicates(gameData.homeworlds, 'homeworlds');
    checkDuplicates(gameData.birthrights, 'birthrights');
    checkDuplicates(gameData.lures, 'lures');
    checkDuplicates(gameData.trials, 'trials');
    checkDuplicates(gameData.motivations, 'motivations');
    checkDuplicates(gameData.skills, 'skills');
    checkDuplicates(gameData.talents, 'talents');
    checkDuplicates(gameData.weapons, 'weapons');
    checkDuplicates(gameData.armor, 'armor');
    checkDuplicates(gameData.equipment, 'equipment');
    checkDuplicates(gameData.traits, 'traits');

    // Check for missing skill references in careers and homeworlds
    const skillIds = new Set(gameData.skills.map((skill) => (skill as ItemWithId).id));
    const talentIds = new Set(gameData.talents.map((talent) => (talent as ItemWithId).id));
    const traitIds = new Set(gameData.traits.map((trait) => (trait as ItemWithId).id));
    const equipmentIds = new Set([
      ...gameData.weapons.map((w) => (w as ItemWithId).id),
      ...gameData.armor.map((a) => (a as ItemWithId).id),
      ...gameData.equipment.map((e) => (e as ItemWithId).id),
      ...(gameData.shopItems || []).map((item) => (item as ItemWithId).id)
    ]);

    // Check career references
    gameData.careers.forEach((career) => {
      const careerWithRefs = career as ItemWithReferences;
      careerWithRefs.startingSkills?.forEach((skillId: string) => {
        if (!skillIds.has(skillId)) {
          missingReferences.push(
            `Career ${careerWithRefs.id} references missing skill: ${skillId}`
          );
          errors.push(`Career ${careerWithRefs.id} references missing skill: ${skillId}`);
        }
      });
      careerWithRefs.startingTalents?.forEach((talentId: string) => {
        if (!talentIds.has(talentId)) {
          missingReferences.push(
            `Career ${careerWithRefs.id} references missing talent: ${talentId}`
          );
          errors.push(`Career ${careerWithRefs.id} references missing talent: ${talentId}`);
        }
      });
      careerWithRefs.startingEquipment?.forEach((equipmentId: string) => {
        if (!equipmentIds.has(equipmentId)) {
          missingReferences.push(
            `Career ${careerWithRefs.id} references missing equipment: ${equipmentId}`
          );
          errors.push(`Career ${careerWithRefs.id} references missing equipment: ${equipmentId}`);
        }
      });
    });

    // Check homeworld references
    gameData.homeworlds.forEach((homeworld) => {
      const homeworldWithRefs = homeworld as ItemWithReferences;
      homeworldWithRefs.startingSkills?.forEach((skillId: string) => {
        if (!skillIds.has(skillId)) {
          missingReferences.push(
            `Homeworld ${homeworldWithRefs.id} references missing skill: ${skillId}`
          );
          errors.push(`Homeworld ${homeworldWithRefs.id} references missing skill: ${skillId}`);
        }
      });
      homeworldWithRefs.startingTalents?.forEach((talentId: string) => {
        if (!talentIds.has(talentId)) {
          missingReferences.push(
            `Homeworld ${homeworldWithRefs.id} references missing talent: ${talentId}`
          );
          errors.push(`Homeworld ${homeworldWithRefs.id} references missing talent: ${talentId}`);
        }
      });
      homeworldWithRefs.startingTraits?.forEach((traitId: string) => {
        if (!traitIds.has(traitId)) {
          missingReferences.push(
            `Homeworld ${homeworldWithRefs.id} references missing trait: ${traitId}`
          );
          errors.push(`Homeworld ${homeworldWithRefs.id} references missing trait: ${traitId}`);
        }
      });
      homeworldWithRefs.startingEquipment?.forEach((equipmentId: string) => {
        if (!equipmentIds.has(equipmentId)) {
          missingReferences.push(
            `Homeworld ${homeworldWithRefs.id} references missing equipment: ${equipmentId}`
          );
          errors.push(
            `Homeworld ${homeworldWithRefs.id} references missing equipment: ${equipmentId}`
          );
        }
      });
    });

    // Check birthright references
    gameData.birthrights.forEach((birthright) => {
      const birthrightWithRefs = birthright as ItemWithReferences;
      if (birthrightWithRefs.startingEquipment) {
        birthrightWithRefs.startingEquipment.forEach((equipmentId: string) => {
          if (!equipmentIds.has(equipmentId)) {
            missingReferences.push(
              `Birthright ${birthrightWithRefs.id} references missing equipment: ${equipmentId}`
            );
            errors.push(
              `Birthright ${birthrightWithRefs.id} references missing equipment: ${equipmentId}`
            );
          }
        });
      }
    });

    // Check for consistent rulebook references
    const rulebookIds = new Set<string>();
    Object.values(gameData)
      .flat()
      .forEach((item: unknown) => {
        const itemWithRefs = item as ItemWithReferences;
        if (itemWithRefs.rulebook) {
          rulebookIds.add(itemWithRefs.rulebook);
        }
      });

    if (rulebookIds.size === 0) {
      warnings.push('No rulebook references found in data');
    } else if (!rulebookIds.has('rogue-trader-core')) {
      warnings.push('No core rulebook content found');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      duplicateIds,
      missingReferences,
      orphanedReferences
    };
  }

  /**
   * Check if data needs migration
   */
  public needsMigration(data: unknown): boolean {
    const dataWithMeta = data as DataWithMetadata;
    return !dataWithMeta.schemaVersion || dataWithMeta.schemaVersion < this.CURRENT_SCHEMA_VERSION;
  }

  /**
   * Get current schema version
   */
  public getCurrentSchemaVersion(): number {
    return this.CURRENT_SCHEMA_VERSION;
  }

  /**
   * Get current data version
   */
  public getCurrentDataVersion(): string {
    return this.CURRENT_DATA_VERSION;
  }

  /**
   * Compare two semantic versions, returns true if version1 is lower than version2
   */
  private isVersionLower(version1: string, version2: string): boolean {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;
      if (v1 < v2) return true;
      if (v1 > v2) return false;
    }
    return false; // versions are equal
  }
}

// Export singleton instance
export const dataValidationService = DataValidationService.getInstance();
